/**
 * useFilterBreakpoint Hook
 *
 * Manages filter visibility based on viewport breakpoints.
 * - Desktop (>=992px): Filters are always visible
 * - Mobile (<992px): Filters are collapsed by default
 *
 * @param {number} [breakpoint=992] - The breakpoint width in pixels (default: 992px for lg breakpoint)
 * @returns {{showFilters: boolean, setShowFilters: Function}} Filter visibility state and setter
 *
 * @example
 * ```jsx
 * const { showFilters, setShowFilters } = useFilterBreakpoint();
 *
 * return (
 *   <div>
 *     <button onClick={() => setShowFilters(v => !v)}>Toggle Filters</button>
 *     {showFilters && <FilterComponent />}
 *   </div>
 * );
 * ```
 */

import { useState, useEffect } from 'react';
import '../assets/scss/FilterResponsive.scss';

const useFilterBreakpoint = (breakpoint = 992) => {
  const isDesktop = () => window.innerWidth >= breakpoint;
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    // Track whether we were on desktop before this resize
    let wasDesktop = isDesktop();

    // Initialize on mount
    setShowFilters(wasDesktop);

    const handleResize = () => {
      const nowDesktop = isDesktop();

      if (nowDesktop && !wasDesktop) {
        // Crossed from mobile → desktop: always show filters
        setShowFilters(true);
      } else if (!nowDesktop && wasDesktop) {
        // Crossed from desktop → mobile: collapse filters
        setShowFilters(false);
      }
      // No threshold crossing (e.g. virtual keyboard resize on mobile): do nothing

      wasDesktop = nowDesktop;
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return { showFilters, setShowFilters };
};

export default useFilterBreakpoint;
